import type { TourStep, TourConfig } from '@/types/tour'

export type { TourStep, TourConfig }

export const tours: TourConfig[] = [
  // ============================================================
  // AUTH · Login
  // ============================================================
  // (Tour de login eliminado: no aporta valor en una pantalla autoexplicativa)

  // ============================================================
  // DASHBOARDS
  // ============================================================
  {
    id: 'tour-operator-dashboard',
    routeName: 'operator-dashboard',
    title: 'Tu panel de operador',
    roles: ['operator', 'admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Este es tu panel personal. Aquí ves tu actividad de hoy en un vistazo.',
        position: 'bottom',
      },
      {
        // KpiStrip
        element: '.grid.gap-3:first-of-type, [data-tour="kpi-strip"]',
        intro: 'Tus KPIs de hoy: llamadas, leads asignados, ventas, tiempo medio de llamada y callbacks pendientes.',
        position: 'bottom',
      },
      {
        // Botón Nuevo Lead
        element: 'button:has(.lucide-plus), [data-tour="btn-new-lead"]',
        intro: 'Desde este botón creas un lead nuevo en el sistema.',
        position: 'left',
      },
      {
        // Card Leads asignados
        element: '.card:has(h2:contains("Leads asignados")), [data-tour="card-assigned-leads"]',
        intro: 'Aquí ves los leads que tienes asignados. Cada uno muestra prioridad, nombre, teléfono y estado.',
        position: 'right',
      },
      {
        // Card Callbacks
        element: '.card:has(h2:contains("Callbacks")), [data-tour="card-callbacks"]',
        intro: 'Tus próximos callbacks. Pulsa "Ver todos" para abrir el listado completo.',
        position: 'left',
      },
      {
        // Card Acciones rápidas
        element: '.card:has(h2:contains("Acciones")), [data-tour="card-quick-actions"]',
        intro: 'Accesos directos: nuevo lead, mis llamadas y mi calendario.',
        position: 'left',
      },
    ],
  },

  {
    id: 'tour-supervisor-dashboard',
    routeName: 'supervisor-dashboard',
    title: 'Panel de Supervisor',
    roles: ['admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Visión en tiempo real del equipo y las campañas. Aquí controlas la operación.',
        position: 'bottom',
      },
      {
        element: '.grid.gap-3:first-of-type, [data-tour="kpi-strip"]',
        intro: 'KPIs globales: pendientes, llamadas hoy, ventas, agentes online, campañas activas y conversión media.',
        position: 'bottom',
      },
      {
        element: '.card:has(h2:contains("Mural")), [data-tour="card-agents"]',
        intro: 'Mural de agentes. Cada tarjeta muestra estado, campaña, llamadas y ventas del operador.',
        position: 'right',
      },
      {
        element: '.card:has(h2:contains("Campañas")), [data-tour="card-campaigns"]',
        intro: 'Progreso de campañas con anillos: total, llamados, pendientes y conversión.',
        position: 'left',
      },
      {
        element: '.card:has(h2:contains("Actividad")), [data-tour="card-activity"]',
        intro: 'Feed con las últimas acciones del equipo: llamadas, ventas y cambios de estado.',
        position: 'right',
      },
      {
        element: '.card:has(h2:contains("Llamadas por hora")), [data-tour="card-calls-chart"]',
        intro: 'Gráfico de volumen de llamadas por hora. Detecta picos de actividad.',
        position: 'left',
      },
    ],
  },

  {
    id: 'tour-supervisor-dashboard',
    routeName: 'supervisor-dashboard',
    title: 'Dashboard Supervisor',
    roles: ['admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Monitorización en tiempo real. Se actualiza cada 5 segundos.',
        position: 'bottom',
      },
      {
        element: 'button:has(.lucide-refresh-cw), [data-tour="btn-refresh"]',
        intro: 'Pulsa Actualizar para refrescar los datos manualmente.',
        position: 'left',
      },
      {
        element: '[data-tour="kpi-strip"], .grid.gap-2:first-of-type',
        intro: 'Estado del equipo y de las campañas: online, en llamada, llamadas hoy, ventas hoy y campañas activas.',
        position: 'bottom',
      },
      {
        element: 'table, [data-tour="agents-table"]',
        intro: 'Detalle de agentes: estado, campaña, llamadas y ventas.',
        position: 'top',
      },
    ],
  },

  // ============================================================
  // LEADS
  // ============================================================
  {
    id: 'tour-leads-list',
    routeName: 'leads-list',
    title: 'Listado de Leads',
    roles: ['admin', 'operator', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Aquí ves todos los leads del sistema. Filtra y busca para encontrar lo que necesitas.',
        position: 'bottom',
      },
      {
        // TODO: add data-tour="btn-new" in LeadsListView.vue
        element: '[data-tour="btn-new"], button:has(.lucide-plus)',
        intro: 'Desde este botón creas un lead nuevo.',
        position: 'left',
      },
      {
        // Filtros card
        element: '.card:has(.flex.flex-wrap), [data-tour="filters"]',
        intro: 'Filtra por texto, estado, prioridad, campaña, zona, operador y fuente. Se aplica en tiempo real.',
        position: 'bottom',
      },
      {
        element: 'table thead, [data-tour="table-head"]',
        intro: 'Clic en una columna para ordenar. La paginación está al pie.',
        position: 'bottom',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Pasa el ratón por una fila para ver las acciones: simular, llamar y editar.',
        position: 'right',
      },
    ],
  },

  {
    id: 'tour-lead-create',
    routeName: 'lead-create',
    title: 'Crear nuevo Lead',
    roles: ['admin', 'operator', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Rellena los datos del nuevo lead. El formulario está dividido en pestañas.',
        position: 'bottom',
      },
      {
        // Tabs
        element: '[role="tablist"], [data-tour="tabs"]',
        intro: 'Navega entre secciones: Básico, Contacto, Dirección, Seguro, Familia, Agenda y Resultados.',
        position: 'bottom',
      },
      {
        // TODO: add data-tour="btn-submit" in LeadFormView.vue
        element: '[data-tour="btn-submit"], button[type="submit"]',
        intro: 'Cuando termines, pulsa Guardar. Si quieres salir sin guardar, pulsa Cancelar.',
        position: 'top',
      },
    ],
  },

  {
    id: 'tour-lead-detail',
    routeName: 'lead-detail',
    title: 'Detalle de Lead',
    roles: ['admin', 'operator', 'manager'],
    steps: [
      {
        element: '[data-tour="header-card"]',
        intro: 'Ficha completa del lead. Aquí gestionas toda su información.',
        position: 'bottom',
      },
      {
        // Botones acción cabecera (Llamar, Editar)
        element: '[data-tour="header-card"] button',
        intro: 'Simular tarifas, llamar al lead o editar sus datos.',
        position: 'left',
      },
      {
        element: '[data-tour="tabs"]',
        intro: 'Pestañas: Contacto, Dirección, Seguro, Familia, Agenda, Historial y Documentos.',
        position: 'bottom',
      },
      {
        // Panel lateral acciones rápidas
        element: '[data-tour="card-actions"]',
        intro: 'Acciones rápidas: agendar callback, registrar llamada, marcar como no interesado.',
        position: 'left',
      },
      {
        // Panel lateral estadísticas
        element: '[data-tour="card-stats"]',
        intro: 'Estadísticas del lead: llamadas totales y documentos adjuntos.',
        position: 'left',
      },
      {
        // Panel lateral comentarios
        element: '[data-tour="card-comments"]',
        intro: 'Añade notas internas sobre el lead. Solo las ve tu equipo.',
        position: 'left',
      },
    ],
  },

  {
    id: 'tour-lead-edit',
    routeName: 'lead-edit',
    title: 'Editar Lead',
    roles: ['admin', 'operator', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Edita los datos del lead. Navega por las pestañas para modificar cada sección.',
        position: 'bottom',
      },
      {
        element: '[role="tablist"], [data-tour="tabs"]',
        intro: 'Secciones: Básico, Contacto, Dirección, Seguro, Familia, Agenda y Resultados.',
        position: 'bottom',
      },
      {
        element: '[data-tour="btn-submit"], button[type="submit"]',
        intro: 'Pulsa Guardar para aplicar los cambios o Cancelar para descartarlos.',
        position: 'top',
      },
    ],
  },

  {
    id: 'tour-my-leads',
    routeName: 'my-leads',
    title: 'Mis Leads',
    roles: ['operator', 'admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Aquí ves los leads que tienes asignados. Filtra por estado y prioridad.',
        position: 'bottom',
      },
      {
        element: '.card:has(.flex.flex-wrap), [data-tour="filters"]',
        intro: 'Filtra tus leads por estado y prioridad para organizar tu trabajo.',
        position: 'bottom',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Acciones por lead: simular, llamar y editar. Pasa el ratón para verlas.',
        position: 'right',
      },
    ],
  },

  {
    id: 'tour-my-callbacks',
    routeName: 'my-callbacks',
    title: 'Mis Callbacks',
    roles: ['operator', 'admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Leads con callback programado. Planifica tus devoluciones desde aquí.',
        position: 'bottom',
      },
      {
        // Botón Limpiar
        element: 'button:has(.lucide-x), [data-tour="btn-clear"]',
        intro: 'Pulsa Limpiar para resetear los filtros y ver todos tus callbacks.',
        position: 'left',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Cada fila muestra fecha/hora del callback, prioridad y estado. Llama o edita desde aquí.',
        position: 'right',
      },
    ],
  },

  {
    id: 'tour-calendar',
    routeName: 'calendar',
    title: 'Calendario de Leads',
    roles: ['operator', 'admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Tus leads asignados en dos vistas: Calendario y Kanban.',
        position: 'bottom',
      },
      {
        element: '[role="tablist"]',
        intro: 'Cambia entre Calendario y Kanban para ver tus leads de distintas formas.',
        position: 'bottom',
      },
      {
        element: 'button:has(.lucide-plus), [data-tour="btn-new"]',
        intro: 'Crea un nuevo lead desde aquí.',
        position: 'left',
      },
    ],
  },

  // ============================================================
  // CAMPAIGNS
  // ============================================================
  {
    id: 'tour-campaigns-list',
    routeName: 'campaigns-list',
    title: 'Campañas',
    roles: ['admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Aquí ves todas las campañas. Cada una agrupa leads de una misma fuente.',
        position: 'bottom',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Cada campaña muestra fuente, estado, fechas, prioridad y progreso. Edita o elimina desde aquí.',
        position: 'right',
      },
    ],
  },

  {
    id: 'tour-campaign-detail',
    routeName: 'campaign-detail',
    title: 'Detalle de Campaña',
    roles: ['admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Ficha de la campaña con sus estadísticas y leads.',
        position: 'bottom',
      },
      {
        element: '[data-tour="btn-edit"], button:has(.lucide-pencil)',
        intro: 'Edita los datos de la campaña o elimínala si ya no es necesaria.',
        position: 'left',
      },
    ],
  },

  // ============================================================
  // CALLS
  // ============================================================
  {
    id: 'tour-calls-list',
    routeName: 'calls-list',
    title: 'Llamadas',
    roles: ['admin', 'operator', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Histórico de llamadas. Filtra por tipo, estado, fecha y operador.',
        position: 'bottom',
      },
      {
        element: '.card:has(.flex.flex-wrap), [data-tour="filters"]',
        intro: 'Filtra para encontrar llamadas concretas. Útil para auditar actividad.',
        position: 'bottom',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Cada llamada muestra número, tipo, estado, duración, operador y fecha.',
        position: 'right',
      },
    ],
  },

  // ============================================================
  // USERS
  // ============================================================
  {
    id: 'tour-users-list',
    routeName: 'users-list',
    title: 'Usuarios',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Gestión de usuarios del sistema. Aquí das de alta operadores y managers.',
        position: 'bottom',
      },
      {
        // TODO: add data-tour="btn-new" in UsersListView.vue
        element: '[data-tour="btn-new"], button:has(.lucide-plus)',
        intro: 'Crea un nuevo usuario. Asigna el rol adecuado: admin, manager, operador o generator.',
        position: 'left',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Acciones por usuario: editar, resetear contraseña y activar/desactivar.',
        position: 'right',
      },
    ],
  },

  // ============================================================
  // ADMIN
  // ============================================================
  {
    id: 'tour-admin-audit',
    routeName: 'admin-audit',
    title: 'Logs de Auditoría',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Registro de todas las acciones del sistema. Auditoría completa.',
        position: 'bottom',
      },
      {
        element: '.card:has(.flex.flex-wrap), [data-tour="filters"]',
        intro: 'Filtra por usuario, acción y fecha para encontrar eventos concretos.',
        position: 'bottom',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Clic en una fila para ver el detalle completo de la acción.',
        position: 'right',
      },
    ],
  },

  {
    id: 'tour-admin-call-results',
    routeName: 'admin-call-results',
    title: 'Resultados de Llamada',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Configura los posibles resultados de una llamada (interesado, no contesta, devolución...).',
        position: 'bottom',
      },
      {
        element: '[data-tour="btn-new"], button:has(.lucide-plus)',
        intro: 'Crea un nuevo resultado de llamada.',
        position: 'left',
      },
    ],
  },

  {
    id: 'tour-admin-lead-statuses',
    routeName: 'admin-lead-statuses',
    title: 'Estados de Lead',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Define los estados posibles de un lead (nuevo, en progreso, convertido...).',
        position: 'bottom',
      },
      {
        element: '[data-tour="btn-new"], button:has(.lucide-plus)',
        intro: 'Crea un nuevo estado de lead.',
        position: 'left',
      },
    ],
  },

  {
    id: 'tour-admin-callback-reminders',
    routeName: 'admin-callback-reminders',
    title: 'Recordatorios Callback',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Gestiona los recordatorios automáticos para callbacks.',
        position: 'bottom',
      },
      {
        element: '.card:has(.flex.flex-wrap), [data-tour="filters"]',
        intro: 'Filtra por estado y fecha para gestionar los recordatorios.',
        position: 'bottom',
      },
    ],
  },

  {
    id: 'tour-admin-templates',
    routeName: 'admin-templates',
    title: 'Gestión de Plantillas',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Gestión de plantillas de comunicación: emails, SMS y scripts.',
        position: 'bottom',
      },
      {
        element: '[data-tour="btn-new"], button:has(.lucide-plus)',
        intro: 'Crea una nueva plantilla.',
        position: 'left',
      },
    ],
  },

  {
    id: 'tour-admin-retention',
    routeName: 'admin-retention',
    title: 'Políticas de Retención',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Define el tiempo de retención de datos por entidad.',
        position: 'bottom',
      },
      {
        element: 'table, [data-tour="retention-table"]',
        intro: 'Tabla de políticas. La vista previa muestra los registros que se eliminarían.',
        position: 'top',
      },
    ],
  },

  {
    id: 'tour-admin-settings',
    routeName: 'admin-settings',
    title: 'Configuración',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Parámetros globales del sistema: integraciones, notificaciones, apariencia y seguridad.',
        position: 'bottom',
      },
    ],
  },

  {
    id: 'tour-admin-system',
    routeName: 'admin-system',
    title: 'Monitor del Sistema',
    roles: ['admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Salud del sistema: estado de servicios, latencia, memoria, disco y conexiones.',
        position: 'bottom',
      },
    ],
  },

  // ============================================================
  // ANALYTICS
  // ============================================================
  {
    id: 'tour-analytics',
    routeName: 'analytics',
    title: 'Analytics y Reportes',
    roles: ['admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Analítica y reportes del equipo. Filtra por fecha, campaña y zona.',
        position: 'bottom',
      },
      {
        element: '.card:has(.flex.flex-wrap), [data-tour="filters"]',
        intro: 'Filtros globales: los cambios se aplican a todos los widgets a la vez.',
        position: 'bottom',
      },
      {
        // TODO: add data-tour="btn-export" in AnalyticsDashboardView.vue
        element: '[data-tour="btn-export"], button:has(.lucide-download)',
        intro: 'Exporta los datos visibles en CSV para reportes offline.',
        position: 'left',
      },
    ],
  },

  // ============================================================
  // GENERATOR
  // ============================================================
  {
    id: 'tour-generator-dashboard',
    routeName: 'generator-dashboard',
    title: 'Mi Panel · Generador',
    roles: ['generator', 'admin'],
    steps: [
      {
        element: 'h1',
        intro: 'Tu panel de generador de leads. Aquí ves los leads que has introducido.',
        position: 'bottom',
      },
      {
        element: 'button:has(.lucide-refresh-cw), [data-tour="btn-refresh"]',
        intro: 'Pulsa para refrescar el listado tras crear nuevos leads.',
        position: 'left',
      },
      {
        element: 'tbody tr:first-child, [data-tour="table-row"]',
        intro: 'Listado de tus leads generados. Pagina al pie si tienes muchos registros.',
        position: 'right',
      },
    ],
  },

  // ============================================================
  // TARIFAS · ZONAS · IMPORT · HELP
  // ============================================================
  {
    id: 'tour-tarifas-list',
    routeName: 'tarifas-list',
    title: 'Tarifas',
    roles: ['admin', 'operator', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Catálogo de tarifas. Edita o elimina productos desde aquí.',
        position: 'bottom',
      },
    ],
  },

  {
    id: 'tour-zonas-list',
    routeName: 'zonas-list',
    title: 'Zonas Geográficas',
    roles: ['admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Listado de zonas con código postal, provincia y descripción.',
        position: 'bottom',
      },
    ],
  },

  {
    id: 'tour-import-leads',
    routeName: 'import-leads',
    title: 'Importar Leads',
    roles: ['admin', 'manager'],
    steps: [
      {
        element: 'h1',
        intro: 'Importa leads en lote desde un CSV.',
        position: 'bottom',
      },
      {
        // TODO: add data-tour="btn-template" in ImportLeadsView.vue
        element: '[data-tour="btn-template"], button:has(.lucide-download)',
        intro: 'Descarga la plantilla con el formato esperado antes de importar.',
        position: 'right',
      },
      {
        // TODO: add data-tour="btn-import" in ImportLeadsView.vue
        element: '[data-tour="btn-import"], button:has(.lucide-upload)',
        intro: 'Sube tu CSV. El sistema valida y crea los registros automáticamente.',
        position: 'left',
      },
    ],
  },

  {
    id: 'tour-help',
    routeName: 'help',
    title: 'Centro de Ayuda',
    roles: ['admin', 'operator', 'manager', 'generator'],
    steps: [
      {
        element: 'h1',
        intro: 'Centro de ayuda y tours interactivos. Selecciona un tour para recorrer una pantalla.',
        position: 'bottom',
      },
    ],
  },
]

export function getTourByRoute(routeName: string): TourConfig | undefined {
  return tours.find((t) => t.routeName === routeName)
}

export function getToursByRole(role: string): TourConfig[] {
  return tours.filter((t) => t.roles.includes(role))
}